import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ToneSlider } from './ToneSlider';
import { useGenerateAssets, useCampaignPlanAssets, useUpdateCampaignPlanAsset } from '@/hooks/useCampaignPlanner';
import { CampaignPlanAsset } from '@/services/campaignPlannerService';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import { ChevronLeft, RefreshCw, Copy, Check, Sparkles, Mail, Share2, Megaphone, MessageSquare, Layout } from 'lucide-react';
import { toast } from 'sonner';

interface PlannerStep3AssetsProps {
  planId: string;
  strategicBrief: any;
  tone: string;
  onToneChange: (tone: string) => void;
  companyId: string;
  onBack: () => void;
  onDone: () => void;
}

const assetCategories = [
  { key: 'email', label: 'Email', icon: Mail, types: ['email_announcement', 'email_reminder', 'email_last_chance'] },
  { key: 'social', label: 'Social', icon: Share2, types: ['social_linkedin', 'social_instagram', 'social_facebook'] },
  { key: 'ads', label: 'Ads', icon: Megaphone, types: ['ad_google', 'ad_meta'] },
  { key: 'sms', label: 'SMS/WhatsApp', icon: MessageSquare, types: ['sms', 'whatsapp'] },
  { key: 'landing', label: 'Landing Page', icon: Layout, types: ['landing_page_brief'] },
];

const charLimits: Record<string, number> = {
  sms: 160,
  whatsapp: 1024,
  social_instagram: 2200,
  social_facebook: 63206,
  social_linkedin: 3000,
  ad_google: 90,
  ad_meta: 125,
};

const typeLabels: Record<string, string> = {
  email_announcement: 'Announcement',
  email_reminder: 'Reminder',
  email_last_chance: 'Last Chance',
  social_linkedin: 'LinkedIn',
  social_instagram: 'Instagram',
  social_facebook: 'Facebook',
  ad_google: 'Google Ads',
  ad_meta: 'Meta Ads',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  landing_page_brief: 'Landing Page Brief',
};

export const PlannerStep3Assets: React.FC<PlannerStep3AssetsProps> = ({
  planId, strategicBrief, tone, onToneChange, companyId, onBack, onDone,
}) => {
  const generateAssets = useGenerateAssets();
  const { data: savedAssets, refetch } = useCampaignPlanAssets(planId);
  const updateAsset = useUpdateCampaignPlanAsset();
  const [assets, setAssets] = useState<CampaignPlanAsset[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('email');

  useEffect(() => {
    if (savedAssets && savedAssets.length > 0) {
      setAssets(savedAssets);
    }
  }, [savedAssets]);

  const generate = async () => {
    try {
      const result = await generateAssets.mutateAsync({ planId, strategicBrief, tone, companyId });
      if (result.assets) {
        setAssets(result.assets);
        refetch();
        toast.success('Assets generated!');
      }
    } catch (e: any) {
      toast.error('Failed to generate assets: ' + (e.message || 'Unknown error'));
    }
  };

  useEffect(() => {
    if (!assets.length && !savedAssets?.length) {
      generate();
    }
  }, []);

  const handleContentChange = (assetId: string, content: string) => {
    setAssets((prev) => prev.map((a) => a.id === assetId ? { ...a, content } : a));
  };

  const handleSave = async (asset: CampaignPlanAsset) => {
    try {
      await updateAsset.mutateAsync({ id: asset.id, updates: { content: asset.content } });
      toast.success('Saved');
    } catch {
      toast.error('Failed to save');
    }
  };

  const handleCopy = (content: string, id: string) => {
    navigator.clipboard.writeText(content);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (generateAssets.isPending && !assets.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <Sparkles className="h-10 w-10 text-primary animate-pulse" />
        <p className="text-sm text-muted-foreground">Generating multi-channel assets...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <ToneSlider value={tone} onChange={onToneChange} />
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={generate} disabled={generateAssets.isPending} className="gap-1.5">
            <RefreshCw className="h-3.5 w-3.5" />
            Regenerate All
          </Button>
        </div>
      </div>

      <ResizablePanelGroup direction="horizontal" className="min-h-[400px] rounded-lg border">
        {/* Brief Panel */}
        <ResizablePanel defaultSize={30} minSize={20}>
          <div className="p-4 space-y-3 h-full overflow-y-auto">
            <h3 className="text-sm font-semibold">Strategic Brief</h3>
            {strategicBrief.hook && (
              <div><p className="text-xs font-medium text-amber-600">Hook</p><p className="text-xs text-muted-foreground">{strategicBrief.hook}</p></div>
            )}
            {strategicBrief.target_segments && (
              <div><p className="text-xs font-medium text-blue-600">Segments</p><p className="text-xs text-muted-foreground">{Array.isArray(strategicBrief.target_segments) ? strategicBrief.target_segments.join(', ') : strategicBrief.target_segments}</p></div>
            )}
            {strategicBrief.core_offer && (
              <div><p className="text-xs font-medium text-green-600">Offer</p><p className="text-xs text-muted-foreground">{strategicBrief.core_offer}</p></div>
            )}
            {strategicBrief.timeline && (
              <div><p className="text-xs font-medium text-purple-600">Timeline</p><p className="text-xs text-muted-foreground">{strategicBrief.timeline}</p></div>
            )}
          </div>
        </ResizablePanel>
        <ResizableHandle withHandle />
        {/* Assets Panel */}
        <ResizablePanel defaultSize={70} minSize={40}>
          <div className="p-4 h-full overflow-y-auto">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4 flex-wrap h-auto">
                {assetCategories.map((cat) => (
                  <TabsTrigger key={cat.key} value={cat.key} className="gap-1.5 text-xs">
                    <cat.icon className="h-3.5 w-3.5" />
                    {cat.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              {assetCategories.map((cat) => (
                <TabsContent key={cat.key} value={cat.key} className="space-y-4">
                  {cat.types.map((type) => {
                    const asset = assets.find((a) => a.asset_type === type);
                    if (!asset) return (
                      <Card key={type}>
                        <CardContent className="py-6 text-center text-sm text-muted-foreground">
                          No {typeLabels[type]} content generated yet.
                        </CardContent>
                      </Card>
                    );
                    const limit = charLimits[type];
                    const overLimit = limit && asset.content.length > limit;
                    return (
                      <Card key={type}>
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm">{typeLabels[type] || type}</CardTitle>
                            <div className="flex items-center gap-2">
                              {limit && (
                                <Badge variant={overLimit ? 'destructive' : 'outline'} className="text-[10px]">
                                  {asset.content.length}/{limit}
                                </Badge>
                              )}
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleCopy(asset.content, asset.id)}>
                                {copiedId === asset.id ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          {asset.title && <p className="text-xs font-medium">{asset.title}</p>}
                          <Textarea
                            value={asset.content}
                            onChange={(e) => handleContentChange(asset.id, e.target.value)}
                            onBlur={() => handleSave(asset)}
                            className="min-h-[120px] text-sm"
                          />
                        </CardContent>
                      </Card>
                    );
                  })}
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack} className="gap-2">
          <ChevronLeft className="h-4 w-4" />
          Back
        </Button>
        <Button onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
};
